import React, { useState, useEffect, useRef } from "react";

const BlockchainBackground = () => {
    const [nodes, setNodes] = useState([]);
    const [hexagons, setHexagons] = useState([]);
    const [lockIcons, setLockIcons] = useState([]);
    const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
    const containerRef = useRef(null);
    const mouseRef = useRef(null);
    const animationFrameRef = useRef(null);
    
    useEffect(() => {
        generateHexagonalNetwork();
        
        // Set up interval to regenerate some hexagons and locks
        const hexInterval = setInterval(() => {
            updateRandomHexagons();
        }, 5000);
        
        const lockInterval = setInterval(() => {
            updateLockIcons();
        }, 7000);
        
        return () => {
            clearInterval(hexInterval);
            clearInterval(lockInterval);
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, []);

    //Structured hexagonal grid
    const generateHexagonalNetwork = () => {
        const nodesArray = [];
        const hexArray = [];
        const rows = 6;
        const cols = 8;
        
        for (let row = 0; row < rows; row++) {
            const isOddRow = row % 2 === 1;
            const colOffset = isOddRow ? 6 : 0;
            
            for (let col = 0; col < cols; col++) {
                const x = (col * 12) + colOffset + 6; 
                const y = (row * 15) + 5;
                
                // Add random velocity for mesh movement
                nodesArray.push({
                    id: row * cols + col,
                    x: x,
                    y: y,
                    baseX: x, 
                    baseY: y, 
                    velocityX: (Math.random() - 0.5) * 0.1, // Random velocity for x 
                    velocityY: (Math.random() - 0.5) * 0.1, // Random velocity for y 
                    size: 4,
                    opacity: 0.8,
                    color: Math.random() > 0.7 ? '#3B82F6' : '#60A5FA', //Blue shades
                    elevation: Math.random() * 5, 
                });
            }
        }
        
 
        for (let i = 0; i < 18; i++) {
            hexArray.push(createHexagon(i));
        }
        
        setNodes(nodesArray);
        setHexagons(hexArray);

        
        startNodeAnimation();
    };
    
    const createHexagon = (id) => {
        const size = Math.random() * 30 + 40;
        return {
            id: id,
            x: Math.random() * 90 + 5,
            y: Math.random() * 90 + 5,
            size: size,
            rotation: Math.random() * 30 - 15,
            opacity: Math.random() * 0.12 + 0.03,
            elevation: Math.random() * 8,
            appearing: true,
            lifespan: Math.random() * 15000 + 10000, 
            birthTime: Date.now(),
        };
    };
    
    
    const createLockIcon = (id) => {
        return {
            id: id,
            x: Math.random() * 80 + 10,
            y: Math.random() * 80 + 10,
            size: Math.random() * 50 + 50,
            rotation: Math.random() * 360,
            opacity: 0,
            targetOpacity: Math.random() * 0.08 + 0.02,
            appearing: true,
            lifespan: Math.random() * 8000 + 7000, 
            birthTime: Date.now(),
        };
    };
    
    const updateRandomHexagons = () => {
        setHexagons(prevHexagons => {
            return prevHexagons.map(hex => {
                // For some hexagons, start disappearing
                if (Date.now() - hex.birthTime > hex.lifespan && hex.appearing) {
                    return { ...hex, appearing: false };
                }
                
                // Replace disappeared hexagons
                if (!hex.appearing && hex.opacity <= 0.01) {
                    return createHexagon(hex.id);
                }
                
                return hex;
            });
        });
    };
    
    const updateLockIcons = () => {
        setLockIcons(prevIcons => {
            return prevIcons.map(icon => {
                // For some icons, start disappearing
                if (Date.now() - icon.birthTime > icon.lifespan && icon.appearing) {
                    return { ...icon, appearing: false };
                }
                
                // Replace disappeared icons
                if (!icon.appearing && icon.opacity <= 0.01) {
                    return createLockIcon(icon.id);
                }
                
                return icon;
            });
        });
    };
    
    const startNodeAnimation = () => {
        const animateNodes = () => {
            setNodes(prevNodes => 
                prevNodes.map(node => {
                    // Move node according to its velocity
                    let newX = node.x + node.velocityX;
                    let newY = node.y + node.velocityY;
                    
                    if (newX < 0 || newX > 100) node.velocityX *= -1;
                    if (newY < 0 || newY > 100) node.velocityY *= -1;
                    
                    newX = Math.max(0, Math.min(100, newX));
                    newY = Math.max(0, Math.min(100, newY));
                    
                    return {
                        ...node,
                        x: newX,
                        y: newY,
                        // Occasionally change velocity slightly for more natural movement
                        velocityX: Math.random() < 0.01 ? node.velocityX + (Math.random() - 0.5) * 0.05 : node.velocityX,
                        velocityY: Math.random() < 0.01 ? node.velocityY + (Math.random() - 0.5) * 0.05 : node.velocityY,
                    };
                })
            );
            
            
            setHexagons(prevHexagons => 
                prevHexagons.map(hex => {
                    let newOpacity = hex.opacity;
                    if (hex.appearing) {
                        newOpacity = Math.min(hex.opacity + 0.005, 0.12);
                    } else {
                        newOpacity = Math.max(hex.opacity - 0.005, 0);
                    }
                    return {
                        ...hex,
                        opacity: newOpacity,
                        // Add slow 3D rotation for better visual effect
                        rotation: hex.rotation + (Math.random() * 0.05)
                    };
                })
            );
            
            setLockIcons(prevIcons => 
                prevIcons.map(icon => {
                    let newOpacity = icon.opacity;
                    if (icon.appearing) {
                        newOpacity = Math.min(icon.opacity + 0.002, icon.targetOpacity);
                    } else {
                        newOpacity = Math.max(icon.opacity - 0.002, 0);
                    }
                    return {
                        ...icon,
                        opacity: newOpacity,
                        rotation: icon.rotation + 0.05 // Slow rotation
                    };
                })
            );
            
            animationFrameRef.current = requestAnimationFrame(animateNodes);
        };
        
        animationFrameRef.current = requestAnimationFrame(animateNodes);
    };

    // Handle mouse movement for interactions
    useEffect(() => {
        const handleMouseMove = (e) => {
            // Update custom cursor position
            if (mouseRef.current) {
                mouseRef.current.style.left = `${e.clientX}px`;
                mouseRef.current.style.top = `${e.clientY}px`;
            }
            
            if (!containerRef.current) return;
            
            const containerRect = containerRef.current.getBoundingClientRect();
            const relativeX = ((e.clientX - containerRect.left) / containerRect.width) * 100;
            const relativeY = ((e.clientY - containerRect.top) / containerRect.height) * 100;
            
            setMousePosition({ x: e.clientX, y: e.clientY });
            
            // Update nodes based on mouse position with gentle movement
            setNodes(prevNodes => 
                prevNodes.map(node => {
                    // Calculate distance from mouse
                    const dx = relativeX - node.x;
                    const dy = relativeY - node.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    // Nodes closer to mouse move more, with a max influence radius
                    const influence = Math.max(0, 20 - distance) / 20;
                    
                    // Move away from mouse slightly with smooth damping
                    const moveX = (dx / (distance || 1)) * influence * -2;
                    const moveY = (dy / (distance || 1)) * influence * -2;
                    
                    return {
                        ...node,
                        velocityX: node.velocityX + moveX * 0.01, // Gently adjust velocity instead of position
                        velocityY: node.velocityY + moveY * 0.01,
                    };
                })
            );
        };

        // Handle touch movement for mobile
        const handleTouchMove = (e) => {
            if (!containerRef.current || !e.touches[0]) return;
            
            const touch = e.touches[0];
            
            // Update custom cursor position for touch events
            if (mouseRef.current) {
                mouseRef.current.style.left = `${touch.clientX}px`;
                mouseRef.current.style.top = `${touch.clientY}px`;
            }
            
            const containerRect = containerRef.current.getBoundingClientRect();
            const relativeX = ((touch.clientX - containerRect.left) / containerRect.width) * 100;
            const relativeY = ((touch.clientY - containerRect.top) / containerRect.height) * 100;
            
            setMousePosition({ x: touch.clientX, y: touch.clientY });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('touchmove', handleTouchMove);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    return (
        <>
            {/* Custom cursor effect - blue glowing shadow */}
            <div 
                ref={mouseRef}
                className="fixed rounded-full pointer-events-none z-50 mouse-shadow"
                style={{
                    width: '80px',
                    height: '80px',
                    left: `${mousePosition.x}px`,
                    top: `${mousePosition.y}px`,
                    transform: 'translate(-50%, -50%)'
                }}
            />
            

            {/* 3D Hexagonal blockchain background animation */}
            <div ref={containerRef} className="absolute inset-0 z-0 bg-gradient-to-br from-gray-50 to-gray-200 overflow-hidden">
                {/* Large hexagonal elements in background */}
                {hexagons.map(hex => (
                    <div
                        key={`hex-${hex.id}`}
                        className="absolute hexagon-animate"
                        style={{
                            left: `${hex.x}%`,
                            top: `${hex.y}%`,
                            opacity: hex.opacity,
                            transform: `rotate(${hex.rotation}deg) translateZ(${hex.elevation}px) scale(1.2)`,
                            width: `${hex.size}px`,
                            height: `${hex.size * 0.866}px`, // Height of hexagon based on width
                            clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                            background: 'linear-gradient(135deg, #3B82F6 0%, #1E3A8A 100%)',
                            boxShadow: `0 ${hex.elevation}px ${hex.elevation * 2}px rgba(0,0,0,0.1)`,
                            transition: 'transform 1.5s ease-out, opacity 1.5s ease-in-out'
                        }}
                    />
                ))}

                {/* Network nodes */}
                {nodes.map(node => (
                    <div
                        key={`node-${node.id}`}
                        className="absolute rounded-full transform node-animation"
                        style={{
                            left: `${node.x}%`,
                            top: `${node.y}%`,
                            width: `${node.size}px`,
                            height: `${node.size}px`,
                            opacity: node.opacity,
                            backgroundColor: node.color,
                            boxShadow: `0 0 8px ${node.color}, 0 ${node.elevation}px ${node.elevation}px rgba(0,0,0,0.2)`,
                            transform: `translateZ(${node.elevation}px)`,
                        }}
                    />
                ))}
                

                <svg className="absolute inset-0 w-full h-full">
                    <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.5" />
                        </linearGradient>
                    </defs>
                    {nodes.map((node, index) => {
                        
                        return nodes.map((targetNode, targetIndex) => {
                            if (targetIndex <= index) return null; 
                            
                            const dx = Math.abs(node.x - targetNode.x);
                            const dy = Math.abs(node.y - targetNode.y);
                            const distance = Math.sqrt(dx * dx + dy * dy);
                            
                            // Cnnections in hexagonal pattern - dynamic distance based on movement
                            if (distance < 15) {
                                return (
                                    <line
                                        key={`line-${node.id}-${targetNode.id}`}
                                        x1={`${node.x}%`}
                                        y1={`${node.y}%`}
                                        x2={`${targetNode.x}%`}
                                        y2={`${targetNode.y}%`}
                                        stroke="url(#lineGradient)"
                                        strokeWidth="1"
                                        opacity={1 - (distance / 20)}
                                        className="line-animation"
                                    />
                                );
                            }
                            return null;
                        });
                    })}
                </svg>
                
  
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#3B82F6" strokeWidth="0.5"/>
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>
                </div>
                
         
                {lockIcons.map(icon => (
                    <div
                        key={`lock-${icon.id}`}
                        className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none lock-animate"
                        style={{
                            opacity: icon.opacity,
                            transform: `translate(${icon.x - 50}%, ${icon.y - 50}%) rotate(${icon.rotation}deg)`,
                            transition: 'opacity 1.5s ease-in-out, transform 3s ease-in-out'
                        }}
                    >
                        <svg width={icon.size} height={icon.size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" 
                                  stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" 
                                  stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                ))}
            </div>
        </>
    );
};

export default BlockchainBackground;